import Config from '../_config/config';
import { fetchAcademicDetails } from './academic.service';
import { previewReportHTML } from '@conceptwaves/shared-components';

export interface GradeTemplate {
  id: number;
  templateCode: string;
  templateName: string;
  description: string;
  category: string;
  gradeLevel: string;
  isActive: boolean;
  sortOrder: number;
}

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = (): HeadersInit => {
  const currentUser = localStorage.getItem('currentUser');
  let token = '';
  if (currentUser && currentUser !== 'undefined') {
    try {
      const user = JSON.parse(currentUser);
      token = user.token || '';
    } catch (error) {
      console.error('Failed to parse currentUser:', error);
    }
  }

  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

/**
 * Fetch list of available grade templates
 */
export const fetchGradeTemplates = async (
  category?: string,
  gradeLevel?: string
): Promise<GradeTemplate[]> => {
  try {
    let url = `${Config.nodeSiteUrl}v1/reports/grade-templates`;
    console.log('[fetchGradeTemplates] Base URL:', url);
    const params = new URLSearchParams();

    if (category) params.append('category', category);
    if (gradeLevel) params.append('gradeLevel', gradeLevel);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    console.log('[fetchGradeTemplates] Final URL:', url);
    console.log('[fetchGradeTemplates] Auth headers:', getAuthHeaders());

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    console.log('[fetchGradeTemplates] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[fetchGradeTemplates] Error response:', errorText);
      throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[fetchGradeTemplates] Response data:', data);
    return data.data || [];
  } catch (error) {
    console.error('[fetchGradeTemplates] Error fetching grade templates:', error);
    return [];
  }
};

/**
 * Fetch specific template by code
 */
export const fetchTemplateByCode = async (templateCode: string): Promise<any> => {
  try {
    const url = `${Config.nodeSiteUrl}v1/reports/grade-templates/${templateCode}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Error fetching template:', error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Dossier review & sign-off workflow (report_signoff tables).
// A session snapshots the enriched report data server-side, auto-flags
// anomalies for row-level review, and on certify stores the PDF to S3 so
// the distributed artifact is exactly what the teacher certified.
// ---------------------------------------------------------------------------

export interface SignoffFlag {
  code: string;
  detail: string;
}

export interface SignoffStudent {
  enrollmentId: number;
  studentName: string;
  flags: SignoffFlag[];
  reviewStatus: 'PENDING' | 'REVIEWED' | 'FLAG_ACCEPTED';
  reviewedBy: number | null;
  reviewedAt: string | null;
  remarks: string | null;
}

export interface SignoffSession {
  signoffId: number;
  status: string;
  resumed: boolean;
  snapshotHash: string;
  students: SignoffStudent[];
  totalStudents: number;
  flaggedCount: number;
  reviewedCount: number;
}

export interface SignoffStatusInfo {
  signoffId: number;
  status: string;
  refreshRunId: number | null;
  certifiedBy: number | null;
  certifiedByName: string | null;
  certifiedAt: string | null;
  pdfS3Key: string | null;
  remarks: string | null;
  createdAt: string;
  totalStudents: number;
  reviewedStudents: number;
}

const extractErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    return body?.error?.message || body?.message || fallback;
  } catch {
    return fallback;
  }
};

/** Open (or resume) a review session for the given enriched report data. */
export const startDossierSignoff = async (payload: {
  batchId: number;
  templateId: number;
  filters?: Record<string, any>;
  refreshRunId?: number;
  /**
   * report_audit.Id of the render that produced `students` (the D-… id in the
   * dossier footer). Stored on the sign-off so a certified PDF can be traced
   * back to the exact audit row holding the numbers it was built from.
   */
  reportAuditId?: number;
  students: any[];
}): Promise<SignoffSession> => {
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/start`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to start review session'));
  }
  const result = await response.json();
  return result.data;
};

/** Save row-level review states (REVIEWED / FLAG_ACCEPTED / back to PENDING). */
export const updateSignoffStudents = async (
  signoffId: number,
  updates: Array<{ enrollmentId: number; reviewStatus: string; remarks?: string }>
): Promise<SignoffSession> => {
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/${signoffId}/review-students`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ updates }),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to save review updates'));
  }
  const result = await response.json();
  return result.data;
};

/**
 * Certify the session. `students` proves the data hasn't moved since review
 * started (server re-hashes and 409s on mismatch); `html` is the fully
 * rendered dossier that becomes the stored certified PDF.
 */
export const certifyDossierSignoff = async (
  signoffId: number,
  payload: { students: any[]; html: string; filename?: string; remarks?: string }
): Promise<{ signoffId: number; status: string; pdfS3Key: string; downloadFilename: string }> => {
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/${signoffId}/certify`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to certify dossier'));
  }
  const result = await response.json();
  return result.data;
};

/** Latest sign-off state for (batch, template, filters) — drives download gating. */
export const fetchSignoffStatus = async (params: {
  batchId: number;
  templateId: number;
  filters?: Record<string, any>;
  refreshRunId?: number;
}): Promise<{ latest: SignoffStatusInfo | null; certified: SignoffStatusInfo | null; certifiedIsStale: boolean }> => {
  const query = new URLSearchParams({
    batchId: String(params.batchId),
    templateId: String(params.templateId),
  });
  if (params.filters) query.append('filters', JSON.stringify(params.filters));
  if (typeof params.refreshRunId === 'number') query.append('refreshRunId', String(params.refreshRunId));
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/status?${query.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to fetch sign-off status'));
  }
  const result = await response.json();
  return result.data;
};

/** Download the stored certified PDF (the exact artifact that was certified). */
export const downloadCertifiedPdf = async (signoffId: number, filename?: string): Promise<void> => {
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/${signoffId}/download`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to download certified PDF'));
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `certified-dossier-${signoffId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** Invalidate a session (in review or certified). */
export const revokeDossierSignoff = async (signoffId: number, remarks?: string): Promise<void> => {
  const response = await fetch(`${Config.nodeSiteUrl}v1/reports/signoff/${signoffId}/revoke`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ remarks }),
  });
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, 'Failed to revoke sign-off'));
  }
};

/**
 * Resolve the grade report template for a student and open HTML preview.
 * Reuses the existing resolveStudentTemplate API + fetchAcademicDetails + previewReportHTML.
 *
 * @returns An object with { success, message } — caller can show message on error.
 */
export const openStudentGradeReport = async (
  enquiryId: number,
  batchId: number,
): Promise<{ success: boolean; message: string }> => {
  // 1. Resolve template
  const url = `${Config.nodeSiteUrl}v1/reports/grade-templates/for-student?enquiryId=${enquiryId}`;
  const resp = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
  if (!resp.ok) {
    return { success: false, message: 'Failed to resolve grade template' };
  }
  const templateData = await resp.json();
  const resolved = templateData?.data?.resolvedTemplate;
  if (!resolved) {
    return { success: false, message: 'No grade report template configured for this student' };
  }

  // 2. Fetch academic details for this student by enquiryId
  const academicResp = await fetchAcademicDetails({ enquiryId });
  const students = academicResp?.data || [];
  const student = students.length > 0 ? students[0] : null;
  if (!student) {
    return { success: false, message: 'No academic data found for this student' };
  }

  // 3. Open HTML preview in a new window
  await previewReportHTML(resolved.id, [student]);
  return { success: true, message: '' };
};
